import { useEffect } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { useAppSelector } from 'features/app/hooks';
import { resolveSessionCallCenterCodeWithStorageFallback } from 'features/machine-preferences/helpers';
import { machineCallCenterCodeSelector } from 'features/machine-preferences/store';
import { modeSelector } from 'features/multi-mode/store';
import { ModeStorageService } from 'features/multi-mode/services';
import {
  agentNumberSelector,
  callCenterCodeSelector,
} from 'features/user/store';
import { LoggerService } from 'features/logger/services';

const IdentifyOnUserChange = () => {
  const ldClient = useLDClient();
  const agentNumber = useAppSelector(agentNumberSelector);
  const mode = useAppSelector(modeSelector);
  const homeCallCenterCode = useAppSelector(callCenterCodeSelector);
  const machineCallCenterCode = useAppSelector(machineCallCenterCodeSelector);

  useEffect(() => {
    if (!ldClient) return;

    // Normalize `agentNumber` into a string key and validate it's non-empty.
    const keyString =
      agentNumber === undefined || agentNumber === null
        ? ''
        : String(agentNumber).trim();
    const hasKey = keyString.length > 0;

    // Same resolution as session start; omitted when it cannot be resolved.
    const callCenterCode =
      hasKey && mode
        ? resolveSessionCallCenterCodeWithStorageFallback(
            mode,
            machineCallCenterCode,
            homeCallCenterCode
          ).code
        : null;

    const context: any = {
      kind: 'user',
      // Set anonymous true when we have no key to avoid invalid context
      anonymous: !hasKey,
      // Only include `key` when we have a validated non-empty key string
      ...(hasKey ? { key: keyString, agentNumber } : undefined),
      ...(ModeStorageService.getMode()
        ? { mode: ModeStorageService.getMode() }
        : undefined),
      ...(callCenterCode ? { callCenterCode } : undefined),
    };

    ldClient.identify(context).catch((err: unknown) => {
      LoggerService.error(err);
    });
  }, [ldClient, agentNumber, mode, homeCallCenterCode, machineCallCenterCode]);

  return null;
};

export default IdentifyOnUserChange;
